import { ApolloProvider } from '@apollo/client';
import { cond, matches } from 'lodash/fp';
import T from 'prop-types';
import React, { Suspense, useCallback, useMemo } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import ErrorBoundary from '@skippay/core/components/ErrorBoundary';
import { Provider as GoogleAnalyticsProvider } from '@skippay/core/components/GoogleAnalytics';
import { GA_EVENT, GA_PAGEVIEW } from '@skippay/core/components/GoogleAnalytics/constants';
import { getCustomerAnalyticsData } from '@skippay/core/components/GoogleAnalytics/getCustomerAnalyticsData';
import Loader from '@skippay/core/components/Loader';
import LocationProvider from '@skippay/core/components/LocationProvider';
import NetworkInterceptor from '@skippay/core/components/NetworkInterceptor';
import Wrapped from '@skippay/core/components/Wrapped';
import { captureException, init as initSentry } from '@skippay/core/global/errorTracking';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import { getPHostname } from '@skippay/core/hooks/useUserCountryRelatedInfo';
import bannersModule from '@skippay/core/modules/banners';
import clientInfoModule from '@skippay/core/modules/clientInfo';
import cmsModule from '@skippay/core/modules/cms';
import createExternalScriptsModule from '@skippay/core/modules/externalScripts';
import miscModule from '@skippay/core/modules/misc';
import notificationsModule from '@skippay/core/modules/notifications';
import userModule from '@skippay/core/modules/user';
import { useLocaleContext } from '@skippay/core/providers/LocaleProvider/LocaleProvider';

import { OVERVIEW } from 'constants/routes';
import apolloClient, { createApolloClient } from 'global/apolloClient';
import { JarabotContextOwnershipProvider } from 'jarabot/customerContext/ownership';
import { SelfCareJarabotCustomerContext } from 'jarabot/customerContext/SelfCareJarabotCustomerContext';
import JarabotLoginOffers from 'jarabot/JarabotLoginOffers';
import { JarabotSelfCareTrackingProvider } from 'jarabot/JarabotSelfCareTrackingProvider';
import affiliateModule from 'modules/affiliate';
import authModule from 'modules/auth';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';
import RuntimeErrorScreen from 'screens/RuntimeErrorScreen';
import UnavailableScreen from 'screens/UnavailableScreen';
import history from 'store/history';
import { clarityHandlePageview } from 'utils/clarity';
import selfCareExternalScripts from 'utils/externalScripts';
import {
	sanitizeReduxActions,
	sanitizeReduxState,
	sanitizeSentryEvent,
} from 'utils/sanitizeSentry';

import OnLoadActions from './components/OnLoadActions';
import getStore from '../../store';

const errorTrackerModule = initSentry({
	debug: process.env.NODE_ENV === `development`,
	dsn: `https://9ea113ae7e2d0c76e5db2efeba03a63c@o4511925247016960.ingest.de.sentry.io/4511931654996050`,
	environment: process.env.TARGET_ENV,
	release: process.env.npm_package_version,
	sanitizers: { sanitizeReduxActions, sanitizeReduxState, sanitizeSentryEvent },
});

function ProvidersBrowser(props) {
	const { locale } = useLocaleContext();
	const client = useMemo(() => createApolloClient({ language: locale }), [locale]);
	const { store, persistor } = getStore(
		client,
		[
			affiliateModule,
			authModule,
			userModule,
			clientInfoModule,
			errorTrackerModule,
			createExternalScriptsModule(selfCareExternalScripts),
			miscModule,
			notificationsModule,
			bannersModule,
			cmsModule,
		].filter(Boolean)
	);
	return (
		<LocationProvider
			defaultPrivateRoot={OVERVIEW}
			history={history}
			openInNewTab={useCallback((url) => {
				window.open(url);
			}, [])}
		>
			<Provider store={store}>
				<PersistGate loading={<Loader fullSize />} persistor={persistor}>
					<ErrorBoundary
						fallback={(error) => <RuntimeErrorScreen error={error} />}
						onError={captureException}
					>
						<NetworkInterceptor
							renderError={cond([
								[
									matches({ response: { status: 503 } }),
									() => <UnavailableScreen statusCode="503" />,
								],
								[
									matches({ isAxiosError: true, response: undefined }),
									(axiosError) => {
										const internetConnectionOnLine = window.navigator.onLine;
										// during deploy BE not provides CORS headers, thus requests fails like Network
										// Error. We don't have indication of failing CORS request so we guess that CORS
										// fails when Axios returns Network Error and user is onLine
										if (internetConnectionOnLine) {
											captureException(
												new Error(
													`Received empty response from BE while user is onLine. This indicates failed CORS check or some unpredicted network error`
												),
												{ extra: { axiosError } }
											);
											return <UnavailableScreen statusCode={axiosError.code.toString() || '503'} />;
										}

										return <RuntimeErrorScreen networkError />;
									},
								],
							])}
						>
							<Suspense
								fallback={
									<Loader
										fullSize
										style={{
											// these styles are required to overlay a cookie banner
											backgroundColor: `#fff`,
											zIndex: 2,
										}}
									/>
								}
								id="fullPageLoader"
							>
								<ApolloProvider client={client}>
									<JarabotSelfCareTrackingProvider>
										<InitializedContent>{props.children}</InitializedContent>
									</JarabotSelfCareTrackingProvider>
								</ApolloProvider>
							</Suspense>
						</NetworkInterceptor>
					</ErrorBoundary>
				</PersistGate>
			</Provider>
		</LocationProvider>
	);
}

function InitializedContent({ children }) {
	const { country } = useInitialCountryLocale();

	return (
		<Wrapped
			into={
				process.env.GTM_CONTAINER_TAG &&
				process.env.GTM_CONTAINER_PREVIEW && (
					<GoogleAnalyticsProvider
						options={{
							gtmId: process.env.GTM_CONTAINER_TAG,
							preview: process.env.GTM_CONTAINER_PREVIEW,
							auth: process.env.GTM_CONTAINER_AUTH,
						}}
						onPageview={clarityHandlePageview}
						mapObject={(object) => {
							let o = { ...object };
							o.webType = `web`;
							const pHostname = getPHostname(country);
							if (pHostname && (o.event === GA_PAGEVIEW || o.event === GA_EVENT) && !o.pHostname) {
								o.pHostname = pHostname;
							}
							const customer = apolloClient.readQuery({ query: MyProfileQuery });
							if (customer) {
								o = getCustomerAnalyticsData(o, customer.myProfile);
							}
							return o;
						}}
						country={country}
					/>
				)
			}
		>
			<JarabotContextOwnershipProvider>
				{children}
				<OnLoadActions />
				<SelfCareJarabotCustomerContext />
				<JarabotLoginOffers />
			</JarabotContextOwnershipProvider>
		</Wrapped>
	);
}

InitializedContent.propTypes = {
	children: T.element,
};

ProvidersBrowser.propTypes = {
	children: T.element,
};

export default ProvidersBrowser;
